import React, { useMemo } from 'react';
import { createSelector } from 'reselect';
import { useAppSelector } from 'flow/hooks/useRedux';
import Tooltip from 'flow/components/ui-components/Tooltip';
import useAppContext from 'flow/hooks/useAppContext';
import { selectExtensionsStatuses } from 'flow/selectors/ExtensionSelectors';
import { selectAllQueueMembers } from 'flow/selectors/queue-selectors';
import Queue, { QueueMember } from 'flow/types/Queue';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { createTypedMap, TypedMap } from 'flow/types/TypedMap';
import { extensionStatusIsInCallOrInExternalCall } from 'flow/utils/extension-utils';
import NumberOfItemsInQueuesContainer from '../sub-components/NumberOfItemsInQueuesContainer';
import SupervisorOpenQueuesNumber from '../sub-components/SupervisorOpenQueuesNumber';
import SupervisorTotalContainer from '../sub-components/SupervisorTotalContainer';
import AgentsLoggedInSoonItem from './AgentsLoggedInSoonItem';
import { selectSelectedQueues } from './SupervisorOverviewSelectors';
import OperatorGroupDropdownMenu from 'flow/components/pbx/supervisor/Menus/OperatorGroupDropdownMenu';

const SupervisorOverviewComponent = () => {
    const { i18next } = useAppContext();

    const selectedQueues = useAppSelector(selectSelectedQueues);

    const selectedQueuesOpen = useMemo(() => {
        return selectedQueues?.filter((queue: TypedMap<Queue>) =>
            queueIsOpen(queue),
        );
    }, [selectedQueues]);

    const numberOfExtensionsInCall = useAppSelector(selectMembersInCallCount);

    const numberOfExtensionsNotInCall = useAppSelector(
        selectMembersNotInCallCount,
    );

    return (
        <div className="m-auto flex h-full w-full max-w-2xl flex-wrap justify-between gap-x-2 gap-y-3 ps-3 pe-1 pb-3">
            <h2 className="flex min-w-fit grow basis-7/12 text-xl font-bold">
                {i18next.t('pbx.queues')}
            </h2>
            <div className="flex w-fit basis-4/12 text-sm">
                <OperatorGroupDropdownMenu />
            </div>
            <Tooltip
                label={i18next.t(
                    'Supervisor.Queue.Tooltip.Label.HowManyQueuesIsOpen',
                )}
                placement="bottom-start"
            >
                <SupervisorOpenQueuesNumber number={selectedQueuesOpen?.size} />
            </Tooltip>
            <div className="flex gap-x-1">
                <Tooltip label={i18next.t('Queue.CallsInQueue.Section.Title')}>
                    <NumberOfItemsInQueuesContainer />
                </Tooltip>
                <Tooltip
                    label={i18next.t(
                        'Supervisor.Overview.Queue.Members.Available',
                    )}
                >
                    <SupervisorTotalContainer
                        colorType={'green'}
                        iconType={'headset'}
                        label={numberOfExtensionsNotInCall}
                    />
                </Tooltip>
                <Tooltip
                    label={i18next.t(
                        'Supervisor.Overview.Queue.Members.InCall',
                    )}
                >
                    <SupervisorTotalContainer
                        colorType={'red'}
                        iconType={'phone_in_talk'}
                        label={numberOfExtensionsInCall}
                    />
                </Tooltip>
                <Tooltip
                    label={i18next.t(
                        'Supervisor.Overview.Queue.Members.BackSoon',
                    )}
                    placement="bottom-end"
                >
                    <AgentsLoggedInSoonItem />
                </Tooltip>
            </div>
        </div>
    );
};

export default React.memo(SupervisorOverviewComponent);

const queueIsOpen = (queue: TypedMap<Queue>) =>
    queue?.getIn(['activeProfile', 'available']) === true;

const memberIsLoggedInToQueue = (member: TypedMap<QueueMember>) =>
    member.get('loggedIn') === true;

const selectUniqueMembersLoggedInToSomeOpenQueue = createSelector(
    [selectSelectedQueues, selectAllQueueMembers],
    (selectedQueues, allQueueMembers) => {
        const selectedQueuesOpen = selectedQueues?.filter(queueIsOpen);
        return selectedQueuesOpen?.reduce((acc, queue) => {
            const queueMembers = allQueueMembers.get(queue.get('key'));
            return (
                queueMembers?.reduce((memberAcc, member) => {
                    const extensionKey = member.get('extensionKey');
                    return extensionKey && memberIsLoggedInToQueue(member)
                        ? memberAcc.set(extensionKey, member)
                        : memberAcc;
                }, acc) ?? acc
            );
        }, createTypedMap<Record<ExtensionKey, TypedMap<QueueMember>>>());
    },
);

const selectMembersInCallCount = createSelector(
    [selectUniqueMembersLoggedInToSomeOpenQueue, selectExtensionsStatuses],
    (uniqueMembersLoggedInToSomeOpenQueue, extensionsStatuses) =>
        uniqueMembersLoggedInToSomeOpenQueue?.filter((member) => {
            const key = member.get('extensionKey');
            const status = extensionsStatuses.get(key);
            return extensionStatusIsInCallOrInExternalCall(status);
        })?.size || 0,
);

const selectMembersNotInCallCount = createSelector(
    [selectUniqueMembersLoggedInToSomeOpenQueue, selectExtensionsStatuses],
    (uniqueMembersLoggedInToSomeOpenQueue, extensionsStatuses) =>
        uniqueMembersLoggedInToSomeOpenQueue?.filter((member) => {
            const key = member.get('extensionKey');
            const status = extensionsStatuses.get(key);
            return !extensionStatusIsInCallOrInExternalCall(status);
        })?.size || 0,
);
